// ─────────────────────────────────────────────────────────────
// ZIFEK — Rôles au menu (comptes Restauration)
// Permet de structurer la carte en sections : entrée, plat,
// dessert, boisson. Stocké dans `product_items.menu_role` (text).
// null / vide = plat non classé (affiché dans « Autres »).
// ─────────────────────────────────────────────────────────────

export type MenuRole = 'entree' | 'plat' | 'dessert' | 'boisson';

export interface MenuRoleDef {
  key: MenuRole;
  /** Libellé au singulier (badge produit). */
  label: string;
  /** Libellé au pluriel (titre de section du menu). */
  sectionLabel: string;
  icon: string;
}

// L'ordre de ce tableau = ordre d'affichage des sections du menu.
export const MENU_ROLES: MenuRoleDef[] = [
  { key: 'entree', label: 'Entrée', sectionLabel: 'Entrées', icon: 'ri-bowl-line' },
  { key: 'plat', label: 'Plat', sectionLabel: 'Plats', icon: 'ri-restaurant-2-line' },
  { key: 'dessert', label: 'Dessert', sectionLabel: 'Desserts', icon: 'ri-cake-3-line' },
  { key: 'boisson', label: 'Boisson', sectionLabel: 'Boissons', icon: 'ri-cup-line' },
];

const ROLE_MAP = new Map(MENU_ROLES.map((r) => [r.key, r]));

/** Valide / normalise une valeur brute en rôle de menu (ou null). */
export function normalizeMenuRole(value: unknown): MenuRole | null {
  const v = String(value ?? '').trim().toLowerCase();
  return ROLE_MAP.has(v as MenuRole) ? (v as MenuRole) : null;
}

/** Libellé singulier d'un rôle ('' si non classé). */
export function menuRoleLabel(value: unknown): string {
  const role = normalizeMenuRole(value);
  return role ? ROLE_MAP.get(role)!.label : '';
}

/** Définition complète d'un rôle (undefined si non classé). */
export function getMenuRoleDef(value: unknown): MenuRoleDef | undefined {
  const role = normalizeMenuRole(value);
  return role ? ROLE_MAP.get(role) : undefined;
}

/** Ordre canonique des sections du menu. */
export function menuRoleOrder(): MenuRole[] {
  return MENU_ROLES.map((r) => r.key);
}